import { createContext, useContext, useState } from 'react';
import { useNotifications } from './NotificationContext';

const AppContext = createContext();

// ---- Notifications ----
const MOCK_NOTIFICATIONS = [
  {
    id: 1, type: 'application', title: 'New Application',
    message: 'Rahul Kumar applied for ML Intern role', time: '2 min ago', read: false,
    link: '/interns', linkLabel: 'View applicant',
    detail: 'Rahul Kumar, a 3rd-year B.Tech CSE student from IIT Patna, applied for the ML Intern position. Portfolio includes 3 Kaggle competitions (top 10%) and an open-source NLP project. AI match score: 92%.',
  },
  {
    id: 2, type: 'milestone', title: 'Intern Milestone',
    message: 'Priya Singh completed Week 4 milestone', time: '18 min ago', read: false,
    link: '/interns', linkLabel: 'View intern',
    detail: 'Priya Singh has completed Week 4 deliverables: shipped the onboarding redesign A/B test, +18% conversion lift. Mentor: Dr. Alok Sharma. Stipend disbursement triggered.',
  },
  {
    id: 3, type: 'payment', title: 'Payment Confirmed',
    message: 'Plus Plan renewal ₹2,999 — success', time: '1 hr ago', read: true,
    link: '/billing', linkLabel: 'View invoice',
    detail: 'Razorpay payment of ₹2,999 for Plus Plan annual renewal completed successfully. Receipt: RZP_2026_8472. GST invoice emailed to billing@techvision.in.',
  },
  {
    id: 4, type: 'match', title: 'AI Match Digest',
    message: '15 new intern profiles matched your openings', time: '3 hr ago', read: false,
    link: '/interns', linkLabel: 'View matches',
    detail: '15 fresh profiles matched your "Frontend Developer Intern" and "Data Analyst" openings overnight. Top 3 candidates have >95% portfolio fit.',
  },
  {
    id: 5, type: 'certificate', title: 'Certificate Issued',
    message: 'Internship letter issued for Arjun Sharma', time: '5 hr ago', read: true,
    link: '/interns', linkLabel: 'View certificate',
    detail: 'Internship completion certificate issued to Arjun Sharma for his 6-month tenure as a Backend Engineering Intern. Performance: Exceeds Expectations.',
  },
  {
    id: 6, type: 'event', title: 'Event Registration',
    message: '23 registrations for your upcoming hackathon', time: '1 day ago', read: true,
    link: '/events', linkLabel: 'View event',
    detail: '23 confirmed registrations for Bihar Startup Hackathon 2026 (Apr 15-16). Mix of: 12 dev, 7 product, 4 design. Need to confirm venue catering by Apr 10.',
  },
  {
    id: 7, type: 'mentor', title: 'New Mentor Idea Submitted',
    message: 'You suggested "Founder Storytelling Workshop" to Dr. Alok Sharma', time: '1 day ago', read: false,
    link: '/mentors?tab=ideas&id=i1', linkLabel: 'Open Ideas inbox',
    detail: 'Your suggestion "Founder Storytelling Workshop" has been delivered to Dr. Alok Sharma. He typically responds within 48 hours. If accepted, a new batch will be opened for enrollment.',
  },
  {
    id: 8, type: 'mentor', title: 'New Batch Started',
    message: 'Dr. Alok Sharma started "AI Product Roadmap" Batch #3', time: '2 days ago', read: false,
    link: '/mentors?tab=batches&id=b1', linkLabel: 'Open batch',
    detail: 'Batch #3 of "AI Product Roadmap Bootcamp" is now ongoing with 8 enrolled founders. First session was rated 4.9/5 average. Next session: Wed, 7pm IST.',
  },
  {
    id: 9, type: 'mentor', title: 'Batch Feedback Available',
    message: '4 new reviews on "Growth Marketing Mastery" Batch #2', time: '3 days ago', read: true,
    link: '/mentors?tab=batches&id=b3', linkLabel: 'View batch reviews',
    detail: '4 founders left detailed reviews on Nisha Gupta\'s Growth Marketing batch. Average rating: 4.7/5. Common praise: "actionable D2C frameworks". Common ask: "more case studies".',
  },
];

// ---- Programs (hosted by mentors, assigned to companies based on portfolio match) ----
const MOCK_PROGRAMS = [
  {
    id: 'p1',
    title: 'AI Product Roadmap Bootcamp',
    description: 'A 6-week intensive on building AI-first product strategy, roadmapping, and OKR alignment for early-stage AI startups.',
    mentorId: 1, mentorName: 'Dr. Alok Sharma',
    portfolioTags: ['AI/ML & Deep Tech', 'AI Strategy', 'Product Management'],
    duration: '6 weeks', sessionsPerBatch: 12, ratePerSeat: 12000,
    status: 'active', assignedCompanies: ['TechVision AI', 'NeuralEdge', 'BiharBot Labs'],
    totalBatches: 3, activeBatches: 1, completedBatches: 2,
    totalEnrolled: 24, avgRating: 4.85, totalRevenue: 288000,
    createdAt: 'Feb 1, 2026',
  },
  {
    id: 'p2',
    title: 'Series A Fundraising Masterclass',
    description: 'Direct insights from a VC partner on building investor decks, term sheet negotiations, and managing a 12-week fundraise.',
    mentorId: 3, mentorName: 'Rajiv Mehta',
    portfolioTags: ['Fundraising', 'VC Relations', 'Finance & CFO'],
    duration: '4 weeks', sessionsPerBatch: 8, ratePerSeat: 25000,
    status: 'active', assignedCompanies: ['TechVision AI'],
    totalBatches: 1, activeBatches: 0, completedBatches: 1,
    totalEnrolled: 5, avgRating: 4.8, totalRevenue: 125000,
    createdAt: 'Mar 10, 2026',
  },
  {
    id: 'p3',
    title: 'Growth Marketing Mastery (D2C)',
    description: 'Performance marketing playbook covering paid acquisition, CRO, retention loops, and content moats. Tailored for D2C and consumer SaaS.',
    mentorId: 2, mentorName: 'Nisha Gupta',
    portfolioTags: ['Growth Marketing', 'Brand Building', 'E-commerce'],
    duration: '5 weeks', sessionsPerBatch: 10, ratePerSeat: 9000,
    status: 'active', assignedCompanies: ['TechVision AI'],
    totalBatches: 2, activeBatches: 1, completedBatches: 1,
    totalEnrolled: 14, avgRating: 4.7, totalRevenue: 126000,
    createdAt: 'Feb 20, 2026',
  },
  {
    id: 'p4',
    title: 'HR & Remote Culture Playbook',
    description: 'Build scalable HR processes, performance reviews, and a strong remote-first culture from 0-50 employees.',
    mentorId: 4, mentorName: 'Priya Chatterjee',
    portfolioTags: ['HR & Culture Building', 'Operations & Scaling'],
    duration: '3 weeks', sessionsPerBatch: 6, ratePerSeat: 6000,
    status: 'draft', assignedCompanies: [],
    totalBatches: 0, activeBatches: 0, completedBatches: 0,
    totalEnrolled: 0, avgRating: 0, totalRevenue: 0,
    createdAt: 'Apr 25, 2026',
  },
];

const MOCK_BATCHES = [
  {
    id: 'b1', programId: 'p1', programTitle: 'AI Product Roadmap Bootcamp',
    name: 'Batch #3 — Spring 2026', mentorId: 1, mentorName: 'Dr. Alok Sharma',
    startDate: 'Mar 15, 2026', endDate: 'Apr 26, 2026',
    status: 'ongoing', enrolled: 8, capacity: 10,
    sessionsCompleted: 4, totalSessions: 6, avgRating: 4.9, nps: 92,
    weeklyRatings: [
      { week: 'W1', rating: 4.7 }, { week: 'W2', rating: 4.8 },
      { week: 'W3', rating: 4.9 }, { week: 'W4', rating: 5.0 },
    ],
    participants: [
      { name: 'Ananya Roy', company: 'GreenLeaf AI', rating: 5, feedback: 'Game-changing frameworks. The roadmap canvas alone saved us weeks.' },
      { name: 'Vikram Agarwal', company: 'TechVision AI', rating: 5, feedback: 'Best mentor I\'ve worked with — direct, actionable, no fluff.' },
      { name: 'Karan Mehta', company: 'NeuralEdge', rating: 5, feedback: 'OKR alignment session was worth the full program fee.' },
      { name: 'Sneha Iyer', company: 'BiharBot Labs', rating: 4, feedback: 'Loved the depth. Wish there were 2 more sessions.' },
    ],
  },
  {
    id: 'b2', programId: 'p1', programTitle: 'AI Product Roadmap Bootcamp',
    name: 'Batch #2 — Winter 2025', mentorId: 1, mentorName: 'Dr. Alok Sharma',
    startDate: 'Dec 5, 2025', endDate: 'Jan 16, 2026',
    status: 'completed', enrolled: 9, capacity: 10,
    sessionsCompleted: 6, totalSessions: 6, avgRating: 4.8, nps: 89,
    weeklyRatings: [
      { week: 'W1', rating: 4.6 }, { week: 'W2', rating: 4.7 },
      { week: 'W3', rating: 4.8 }, { week: 'W4', rating: 4.9 },
      { week: 'W5', rating: 4.8 }, { week: 'W6', rating: 5.0 },
    ],
    participants: [
      { name: 'Rohit Sen', company: 'DataMint', rating: 5, feedback: 'Helped us close our Pre-Seed in 6 weeks.' },
      { name: 'Megha Patel', company: 'SchemaAI', rating: 5, feedback: 'Worth every rupee. Highly recommended.' },
      { name: 'Aditya Kumar', company: 'Inferro', rating: 4, feedback: 'Strong content. Pacing could be improved.' },
    ],
  },
  {
    id: 'b3', programId: 'p3', programTitle: 'Growth Marketing Mastery (D2C)',
    name: 'Batch #2 — Q1 2026', mentorId: 2, mentorName: 'Nisha Gupta',
    startDate: 'Feb 20, 2026', endDate: 'Mar 27, 2026',
    status: 'ongoing', enrolled: 7, capacity: 12,
    sessionsCompleted: 7, totalSessions: 10, avgRating: 4.7, nps: 84,
    weeklyRatings: [
      { week: 'W1', rating: 4.5 }, { week: 'W2', rating: 4.7 },
      { week: 'W3', rating: 4.8 }, { week: 'W4', rating: 4.6 },
      { week: 'W5', rating: 4.9 },
    ],
    participants: [
      { name: 'Ananya Roy', company: 'GreenLeaf AI', rating: 5, feedback: 'Nisha\'s D2C playbook is gold. Our CAC dropped 30%.' },
      { name: 'Priya Sharma', company: 'CraftBox', rating: 5, feedback: 'Practical, no-BS marketing wisdom.' },
      { name: 'Karthik R', company: 'EcomEdge', rating: 4, feedback: 'Great frameworks but wanted more agency-side perspective.' },
    ],
  },
  {
    id: 'b4', programId: 'p2', programTitle: 'Series A Fundraising Masterclass',
    name: 'Batch #1 — Spring 2026', mentorId: 3, mentorName: 'Rajiv Mehta',
    startDate: 'Mar 25, 2026', endDate: 'Apr 22, 2026',
    status: 'completed', enrolled: 5, capacity: 6,
    sessionsCompleted: 8, totalSessions: 8, avgRating: 4.8, nps: 91,
    weeklyRatings: [
      { week: 'W1', rating: 4.6 }, { week: 'W2', rating: 4.8 },
      { week: 'W3', rating: 4.9 }, { week: 'W4', rating: 5.0 },
    ],
    participants: [
      { name: 'Vikram Agarwal', company: 'TechVision AI', rating: 5, feedback: 'Term sheet review session paid for itself 100x.' },
      { name: 'Suresh Iyer', company: 'CodeForge', rating: 5, feedback: 'Rajiv\'s VC network alone is worth it.' },
    ],
  },
];

// ---- Founder-submitted Program Ideas (suggestions to mentors) ----
const MOCK_IDEAS = [
  {
    id: 'i1', title: 'Founder Storytelling Workshop',
    description: 'A 2-week intensive on crafting compelling founder narratives for pitching to investors, media, and recruitment.',
    suggestion: 'Could combine elements of public speaking, deck design, and personal branding.',
    submittedBy: 'Vikram Agarwal', submittedTo: 'Dr. Alok Sharma', targetMentorId: 1,
    submittedAt: '1 day ago', status: 'pending',
  },
  {
    id: 'i2', title: 'Bihar D2C Brand Building 101',
    description: 'A bootcamp focused on building D2C brands from Tier-2 cities — sourcing, manufacturing partners, last-mile logistics.',
    suggestion: 'Would love a local Patna case study and tie-up with Bihar MSME network.',
    submittedBy: 'Ananya Roy', submittedTo: 'Nisha Gupta', targetMentorId: 2,
    submittedAt: '4 days ago', status: 'accepted',
  },
];

export const AppProvider = ({ children }) => {
  const [currentPlan, setCurrentPlan] = useState(() => localStorage.getItem('sn-plan') || 'free');
  const [programs] = useState(MOCK_PROGRAMS);
  const [batches]  = useState(MOCK_BATCHES);
  const [ideas, setIdeas] = useState(MOCK_IDEAS);
  const [currentUser] = useState({
    name: 'Vikram Agarwal',
    email: 'vikram@techvision.in',
    company: 'TechVision AI',
    role: 'Founder',
    avatar: null,
    location: 'Patna, Bihar',
    dpiit: true,
    portfolio: ['AI/ML & Deep Tech', 'AI Strategy', 'Product Management', 'Fundraising'],
  });

  // Delegate notification state to NotificationContext
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    addNotification,
  } = useNotifications();

  const submitIdea = ({ title, description, suggestion, targetMentorId, targetMentorName, submitterName }) => {
    const newIdea = {
      id: `i${Date.now()}`,
      title, description, suggestion,
      submittedBy: submitterName || currentUser.name,
      submittedTo: targetMentorName,
      targetMentorId,
      submittedAt: 'just now', status: 'pending',
    };
    setIdeas(prev => [newIdea, ...prev]);
    addNotification({
      type: 'mentor',
      title: 'Idea Sent to Mentor',
      message: `"${title}" delivered to ${targetMentorName}`,
      link: `/mentors?tab=ideas&id=${newIdea.id}`,
      linkLabel: 'Open Ideas inbox',
      detail: `Your suggestion "${title}" was sent to ${targetMentorName}. They typically respond within 48 hours. If accepted, a new batch will be opened for enrollment.`,
    });
    return newIdea;
  };

  const upgradePlan = (plan) => {
    setCurrentPlan(plan);
    localStorage.setItem('sn-plan', plan);
  };

  return (
    <AppContext.Provider value={{
      currentPlan, upgradePlan,
      notifications, markAllRead, markRead, addNotification, unreadCount,
      programs, batches, ideas, submitIdea,
      currentUser,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
